import { useEffect } from 'react';
import { EXPERIMENT_FLAGS } from '@scio3/shared-types';
import { useExperimentVariant } from '@/features/experiments';
import {
  hasPinnedArm,
  isBillingExperimentOn,
  rememberDecidedArm,
  seedDecidedArm,
  setDecidedArm,
} from './is-billing-experiment-on';
import { useAuthStore } from '@/stores/auth-store';

/**
 * The one place the `sub-update-v1` arm is resolved, and the seam between the decider and the
 * component-level switches that consume it.
 *
 * Mounted ONCE, in AppShell. Everything else keeps calling `isBillingExperimentOn()` as a plain
 * synchronous read, because the arm chooses between two component TREES — production's wizard and
 * paywall against the copies in this folder — and those switch points are ordinary conditionals in
 * render (`AppShell`, `BillingDialogs`, `SettingsLayout`, `UserMenu`). Converting each of them to
 * its own hook call would mean four resolves and four independent hold states for one company-level
 * fact that cannot differ between them.
 *
 * `useExperimentVariant` does the real work: sticky (an arm already on
 * `company.experiments['sub-update-v1']` short-circuits), otherwise one `resolveExperimentVariant`
 * mutation which decides server-side on companyId and persists write-once. It also honours the
 * generic `?force_variant_sub-update-v1=<arm>` QA override.
 *
 * Returns `isLoading` so the two surfaces that would visibly flash can hold:
 *   - AppShell's lapsed-account branch, which picks between an early-return dialog and a paywall
 *     over the live shell. Getting that wrong shows the wrong arm's paywall, then swaps it.
 *   - BillingDialogs, which picks which wizard to mount.
 * The two nav entries (`SettingsLayout`, `UserMenu`) do not hold: they only differ for an inactive
 * account, and that path is already held by AppShell above them.
 */
export function useBillingExperimentArm(): { isOn: boolean; isLoading: boolean } {
  const { variant, isLoading } = useExperimentVariant(EXPERIMENT_FLAGS.SUB_UPDATE);
  const accountId = useAuthStore((s) => s.company?._id);

  /**
   * Seed the arm this account already resolved to, BEFORE the decider answers.
   *
   * Assignments live in Postgres, so the `company.experiments` short-circuit in
   * `useExperimentVariant` never fires and the decider is re-consulted on every page load. Without
   * this, every one of those loads serves the build default — control — until the round trip
   * lands.
   *
   * In an effect, not during render: `seedDecidedArm` writes module state AND stamps
   * `<html data-sub-update-arm>`, and a DOM write during render is a commit-phase action done in
   * the wrong phase. An effect is still far ahead of the decider, which is a network round trip,
   * so the window this closes is unchanged.
   */
  useEffect(() => {
    if (accountId) seedDecidedArm(accountId);
  }, [accountId]);

  /**
   * Publishing a resolved value into module state is a side effect, not derived data — the
   * consumers read it synchronously during their own render, so it has to be written after this
   * one commits. `setDecidedArm` also re-stamps `<html data-sub-update-arm>`.
   */
  useEffect(() => {
    if (isLoading) return;
    // Remember first: `setDecidedArm` ignores a null once an arm is known, so a later failed
    // resolve must not be able to erase what this account was actually given.
    rememberDecidedArm(accountId, variant);
    setDecidedArm(variant);
  }, [variant, isLoading, accountId]);

  return {
    isOn: isBillingExperimentOn(),
    // A reviewer with `?sub-update=` pinned is not waiting for anything: their arm already beats
    // the decider, so holding would only delay a page whose answer is settled.
    // Deliberately NOT extended to "settled but still no arm": the client cannot tell a failed
    // resolve from a genuinely not-enrolled account, and holding on the latter would leave those
    // users with no wizard at all. The seed + no-downgrade above already keep a KNOWN arm-b
    // account off control, which is the case that matters.
    isLoading: isLoading && !hasPinnedArm(),
  };
}
